import { createApi } from '@reduxjs/toolkit/query/react';
import { gql } from 'graphql-request';
import { baseQuery } from '@classity-edtech/ui-base';
import { PAYMENT } from '../store/actionTypes';
import { OrderInput, Order } from './models/orders';
import { OrderFullfillmentStatus } from '@classity-edtech/platform-utils';
import {
  Payment,
  PaymentInput,
  OpayPaymentResponse,
  OpayPaymentRequest,
  OpayCheckoutInput,
  OpayCheckoutResponse,
} from './models/payments';

export const payment = createApi({
  reducerPath: PAYMENT,
  baseQuery,
  tagTypes: ['Payment'],
  endpoints: builder => ({
    createOrder: builder.mutation<Order, OrderInput>({
      query: variables => ({
        document: gql`
          mutation CreateOrder($orderInput: OrderInput!) {
            createOrder(orderInput: $orderInput) {
              id
            }
          }
        `,
        variables: {
          orderInput: variables,
        },
      }),
      transformResponse: (response: { createOrder: Order }) => response?.createOrder,
      invalidatesTags: ['Payment'],
    }),

    createPayment: builder.mutation<Payment, PaymentInput>({
      query: variables => ({
        document: gql`
          mutation CreatePayment($paymentInput: PaymentInput!) {
            createPayment(paymentInput: $paymentInput) {
              id
            }
          }
        `,
        variables: {
          paymentInput: variables,
        },
      }),
      transformResponse: (response: { createPayment: Payment }) => response?.createPayment,
      invalidatesTags: ['Payment'],
    }),

    opayBankTransfer: builder.mutation<OpayPaymentResponse, OpayPaymentRequest>({
      query: variables => ({
        document: gql`
          mutation OpayBankTransfer($paymentInput: OpayPaymentInput!) {
            opayBankTransfer(paymentInput: $paymentInput) {
              code
              message
              data {
                reference
                orderNo
                nextAction {
                  actionType
                  transferAccountNumber
                  transferBankName
                  expiredTimestamp
                  remarkCode
                  remarkFlag
                }
                status
                amount {
                  total
                  currency
                }
                paymentAmount {
                  total
                  currency
                }
                fee {
                  total
                  currency
                }
                servicFee {
                  total
                  currency
                }
                cafFee {
                  total
                  currency
                }
                vat {
                  total
                  currency
                }
              }
            }
          }
        `,
        variables: {
          paymentInput: variables,
        },
      }),
      transformResponse: (response: { opayBankTransfer: OpayPaymentResponse }) =>
        response?.opayBankTransfer,
      invalidatesTags: ['Payment'],
    }),

    opayCheckout: builder.mutation<OpayCheckoutResponse, OpayCheckoutInput>({
      query: variables => ({
        document: gql`
          mutation OpayCheckout($paymentInput: OpayCheckoutInput!) {
            opayCheckout(paymentInput: $paymentInput) {
              code
              message
              data {
                reference
                orderNo
                cashierUrl
                status
                amount {
                  total
                  currency
                }
                vat {
                  total
                  currency
                }
              }
            }
          }
        `,
        variables: {
          paymentInput: variables,
        },
      }),
      transformResponse: (response: { opayCheckout: OpayPaymentResponse }) =>
        response?.opayCheckout,
      invalidatesTags: ['Payment'],
    }),

    myOrders: builder.query<
      Order[],
      { page: number; limit: number; status: OrderFullfillmentStatus }
    >({
      query: variables => ({
        document: gql`
          query GetMyOrders($page: Int, $limit: Int, $status: String) {
            getMyOrders(page: $page, limit: $limit, status: $status) {
              id
              currencyCode
              totalAmount
              items {
                products {
                  endTime {
                    dateTime
                    timeZone
                  }
                  startTime {
                    dateTime
                    timeZone
                  }
                  maxAttendees
                  location
                  id
                  code
                  category
                  description {
                    text
                    language
                  }
                  name {
                    displayName
                    language
                  }
                  attendees {
                    email
                    organizer
                    user {
                      id
                      profile {
                        firstname
                        lastname
                        bio
                        username
                        certifications {
                          category
                          issuedByName
                          countryOfOrigin
                        }
                        avatar {
                          contentUrl
                          medium
                        }
                        ids {
                          name
                          value
                        }
                      }
                      email
                      country
                      preferences {
                        knowsLanguages
                      }
                    }
                  }
                  isVirtual
                  recurringEventId
                  calendarId
                  prices {
                    currencies {
                      amount
                      amountInUnit
                      code
                      id
                    }
                    description {
                      language
                      text
                    }
                    id
                    productId
                  }
                }
                prices {
                  currencies {
                    amount
                    code
                    amountInUnit
                    id
                  }
                  description {
                    language
                    text
                  }
                  id
                  productId
                }
              }
            }
          }
        `,
        variables,
      }),
      transformResponse: (response: { getMyOrders: Order[] }) => response?.getMyOrders,
      providesTags: ['Payment'],
    }),

    getPayment: builder.query<Payment, { id: string }>({
      query: variables => ({
        document: gql`
          query GetPayment($id: String!) {
            getPayment(id: $id) {
              meta {
                created
              }
              order {
                totalAmount
                uid
                currencyCode
                paymentStatus
              }
              paymentServiceProvider {
                paymentMethod
              }
            }
          }
        `,
        variables,
      }),
      transformResponse: (response: { getPayment: Payment }) => response?.getPayment,
      providesTags: ['Payment'],
    }),
  }),
});

export const {
  useCreateOrderMutation,
  useMyOrdersQuery,
  useCreatePaymentMutation,
  useOpayBankTransferMutation,
  useOpayCheckoutMutation,
  useGetPaymentQuery,
  useLazyGetPaymentQuery,
} = payment;
